import {
  deleteVehicleModel,
  editVehicleModel,
  addVehicleModel,
} from "@/app/lib/actions/vehicle-models";
import { ItemContent, ItemTitle } from "@/components/ui/item";
import {
  ItemsListCard,
  ItemsListCardSkeleton,
} from "@/components/backoffice/items-list-card";
import {
  useVehicleModelsQuery,
  useVehicleBrandsQuery,
  invalidateVehicleModelsQuery,
} from "@/lib/queries";
import { useQueryClient } from "@tanstack/react-query";
import * as z from "zod";
import {
  addVehicleModelSchema,
  editVehicleModelSchema,
} from "@/lib/schemas/vehicle-models";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Field,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import { toast } from "sonner";

type VehicleModel = {
  id: number;
  name: string;
  brandId: number | null;
  isActive: boolean;
};

function VehicleModelItemsListCard() {
  const queryClient = useQueryClient();
  const vehicleBrandsQuery = useVehicleBrandsQuery({ sort: { name: "asc" } });
  const vehicleModelsQuery = useVehicleModelsQuery({ sort: { name: "asc" } });
  const handleCreate = async (
    vehicleModel: z.infer<typeof addVehicleModelSchema>,
  ) => {
    const response = await addVehicleModel(vehicleModel);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Nouveau modèle de véhicule créé.");
      invalidateVehicleModelsQuery(queryClient);
    }
  };

  const handleUpdate = async (
    vehicleModel: z.infer<typeof editVehicleModelSchema>,
  ) => {
    const response = await editVehicleModel(vehicleModel);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Modèle de véhicule modifié.");
      invalidateVehicleModelsQuery(queryClient);
    }
  };

  const handleDelete = async (id: { id: number }) => {
    const response = await deleteVehicleModel(id);
    if (!response.success) {
      toast.error(response.message);
    } else {
      toast.success("Modèle de véhicule supprimé.");
      invalidateVehicleModelsQuery(queryClient);
    }
  };

  if (vehicleModelsQuery.isPending || vehicleBrandsQuery.isPending)
    return <ItemsListCardSkeleton />;
  const vehicleBrands = vehicleBrandsQuery.data?.vehicleBrands ?? [];
  const vehicleModels = vehicleModelsQuery.data?.vehicleModels ?? [];
  const formattedVehicleBrands = vehicleBrands.map((vb) => ({
    label: vb.name,
    value: vb.id.toString(),
  }));
  return (
    <ItemsListCard<VehicleModel>
      title="Modèles de véhicule"
      items={vehicleModels}
      createSchema={addVehicleModelSchema}
      editSchema={editVehicleModelSchema}
      defaultCreateValues={{ name: "", brandId: null, isActive: true }}
      onCreate={handleCreate}
      onUpdate={handleUpdate}
      onDelete={handleDelete}
      renderItem={(item) => (
        <>
          <ItemContent>
            <ItemTitle>{item.name}</ItemTitle>
          </ItemContent>
        </>
      )}
      renderFormFields={(form) => (
        <FieldGroup>
          <form.Field name="name">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Libellé</FieldLabel>
                  <Input
                    id={field.name}
                    name={field.name}
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-invalid={isInvalid}
                    maxLength={200}
                  />
                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>
          <form.Field name="brandId">
            {(field) => {
              const isInvalid =
                field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field data-invalid={isInvalid}>
                  <FieldLabel htmlFor={field.name}>Marque</FieldLabel>
                  <Combobox
                    items={formattedVehicleBrands}
                    itemToStringValue={(vehicleBrand: {
                      label: string;
                      value: string;
                    }) => vehicleBrand.label}
                    value={
                      formattedVehicleBrands.find(
                        (fvb) => fvb.value === field.state.value?.toString(),
                      )?.label ?? ""
                    }
                    onValueChange={(value) => {
                      field.handleChange(
                        value ? Number(value.value) : undefined,
                      );
                    }}
                  >
                    <ComboboxInput
                      placeholder="Selectionner une marque"
                      aria-invalid={isInvalid}
                    />
                    <ComboboxContent>
                      <ComboboxEmpty>No items found.</ComboboxEmpty>
                      <ComboboxList>
                        {(vehicleBrand) => (
                          <ComboboxItem
                            key={vehicleBrand.value}
                            value={vehicleBrand}
                          >
                            {vehicleBrand.label}
                          </ComboboxItem>
                        )}
                      </ComboboxList>
                    </ComboboxContent>
                  </Combobox>

                  {isInvalid && <FieldError errors={field.state.meta.errors} />}
                </Field>
              );
            }}
          </form.Field>

          <form.Field name="isActive">
            {(field) => (
              <Field>
                <FieldLabel htmlFor={field.name}>Statut</FieldLabel>
                <Switch
                  id="switch"
                  checked={field.state.value}
                  onCheckedChange={field.handleChange}
                />
              </Field>
            )}
          </form.Field>
        </FieldGroup>
      )}
    />
  );
}

export { VehicleModelItemsListCard };
